import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, IconButton, InputBase, Popover, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow,
  TableSortLabel, Tooltip, Typography
} from '@mui/material'
import { Avatar } from '../../../components/Avatar'
import SearchIcon from '@mui/icons-material/Search'
import { useTranslation } from 'react-i18next'
import { ContainerSearch } from 'pages/Atlhetes/components/DataGridHeader/styles'
import { Circle, Visibility } from '@mui/icons-material'
import useResponsive from 'hooks/useResponsive'
import moment from 'moment'
import { TreatEmpty } from 'components/TreatEmpty'

export type AthleteRoutine = {
  name: string
  imagePath: string
  routines: Array<{
    name: string
    isOverdue: boolean
    workoutQuantity: number
    doneWorkoutQuantity: number
    untrainedWorkoutQuantity: number
    engagementPercentage: number
    lastTrainedDate: string
    startDate: string
    endDate: string
  }>
}

type RoutineRow = {
  athleteName: string
  imagePath: string
  routineName: string
  isOverdue: boolean
  workoutQuantity: number
  doneWorkoutQuantity: number
  untrainedWorkoutQuantity: number
  engagementPercentage: number
  lastTrainedDate: string
  startDate: string
  endDate: string
}

interface Column {
  field: keyof RoutineRow
  label: string
  width?: number
  align?: 'right' | 'left' | 'center'
  format?: (value: number) => string
}

type Props = {
  dataRows?: AthleteRoutine[]
}

export const DataGridAthleteTraining = ({ dataRows }: Props) => {
  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(7)
  const [orderBy, setOrderBy] = useState<keyof RoutineRow>('athleteName')
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [searchTerm, setSearchTerm] = useState('')
  const [hoveredAluno, setHoveredAluno] = useState<string | null>(null)
  const [rows, setRows] = useState<RoutineRow[]>([])
  const mdUp = useResponsive('up', 'md')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedRoutine, setSelectedRoutine] = useState<RoutineRow | null>(null)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (dataRows) {
      const flattened = dataRows.flatMap((athlete) =>
        athlete.routines.map((routine) => ({
          athleteName: athlete.name,
          imagePath: athlete.imagePath,
          routineName: routine.name,
          isOverdue: routine.isOverdue,
          workoutQuantity: routine.workoutQuantity,
          doneWorkoutQuantity: routine.doneWorkoutQuantity,
          untrainedWorkoutQuantity: routine.untrainedWorkoutQuantity,
          engagementPercentage: routine.engagementPercentage,
          lastTrainedDate: routine.lastTrainedDate ? moment(routine.lastTrainedDate, 'YYYY-MM-DD').format('DD/MM/YYYY') : ' - ',
          startDate: routine.startDate ? moment(routine.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY') : ' - ',
          endDate: routine.endDate ? moment(routine.endDate, 'YYYY-MM-DD').format('DD/MM/YYYY') : ' - '
        }))
      )
      setRows(flattened)
    }
    if ((anchorEl && !anchorEl.isConnected) ?? (open && mdUp)) {
      handleClosePopover()
    }
  }, [dataRows, anchorEl, mdUp, open])

  const columns: Column[] = [
    { field: 'athleteName', label: 'Aluno', width: 150 },
    { field: 'routineName', label: 'Rotina', width: 160 },
    { field: 'startDate', label: 'Data Início', width: 90, align: 'center' },
    { field: 'engagementPercentage', label: '% Treino', width: 60, align: 'center' },
    { field: 'workoutQuantity', label: 'Quantidade Treinos', width: 60, align: 'center' },
    { field: 'doneWorkoutQuantity', label: 'Dias Treinados', width: 60, align: 'center' },
    { field: 'untrainedWorkoutQuantity', label: 'Dias Não Treinados', width: 60, align: 'center' },
    { field: 'lastTrainedDate', label: 'Último Treino', width: 100, align: 'center' },
    { field: 'endDate', label: 'Data Vencimento', width: 90, align: 'center' }
  ]

  const handleChangePage = (event: unknown, newPage: number) => setPage(newPage)
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const handleRequestSort = (property: keyof RoutineRow) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      row.athleteName.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [rows, searchTerm])

  const handleOpenPopover = (routine: RoutineRow, event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setSelectedRoutine(routine)
    setOpen(true)
  }

  const handleClosePopover = () => {
    setAnchorEl(null)
    setSelectedRoutine(null)
    setOpen(false)
  }

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const aVal = a[orderBy]
      const bVal = b[orderBy]

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order === 'asc' ? aVal - bVal : bVal - aVal
      }
      return order === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [filteredRows, orderBy, order])

  const isEmpty = !dataRows || dataRows.length === 0

  if (isEmpty) {
    return (
      <TreatEmpty
        titleMessage={t('dashboard.emptyState.tableTitle')}
        subTitleMessage={t('dashboard.emptyState.tableMessage')}
      />
    )
  }

  return (
    <>
      <TableContainer sx={{ maxHeight: 550 }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell colSpan={9} sx={{ px: 1, py: 1 }}>
                <Typography fontSize={25} fontWeight="bold" color="#d6500d" textAlign="center">
                  {`${t('general.searchDate.labelRoutinesAthletes')}`}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={9} sx={{ px: 3, py: 1 }}>
                <Box display="flex" justifyContent="flex-end" width="100%">
                  <ContainerSearch width={{ xs: '100%', sm: 650 }}>
                    <IconButton type="button" sx={{ p: '5px' }} aria-label="search">
                      <SearchIcon />
                    </IconButton>
                    <InputBase
                      sx={{ ml: 1, flex: 1 }}
                      placeholder={`${t('search.titleAthlete')}`}
                      onChange={(e) => {
                        setSearchTerm(e.target.value)
                        setPage(0)
                      }}
                    />
                  </ContainerSearch>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              {(mdUp
                ? columns
                : columns.filter(
                  (column) =>
                    column.field === 'athleteName' ||
                    column.field === 'routineName'
                )).map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align}
                  style={{ top: 57, width: column.width }}
                  sx={{ color: '#fff', fontSize: 14 }}
                  sortDirection={orderBy === column.field ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.field}
                    direction={orderBy === column.field ? order : 'asc'}
                    onClick={() => handleRequestSort(column.field)}
                    sx={{ color: '#fff', width: 75 }}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              {!mdUp ? <TableCell /> : ''}
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const start = page * rowsPerPage
              const end = start + rowsPerPage
              const paginatedRows = sortedRows.slice(start, end)

              const grouped: Record<string, RoutineRow[]> = {}
              paginatedRows.forEach((row) => {
                if (!grouped[row.athleteName]) grouped[row.athleteName] = []
                grouped[row.athleteName].push(row)
              })

              const rowsJSX: React.ReactNode[] = []
              Object.entries(grouped).forEach(([athleteName, routines]) => {
                routines.forEach((routine, index) => {
                  rowsJSX.push(
                    <TableRow
                      key={`${athleteName}-${routine.routineName}-${index}`}
                      hover
                      onMouseEnter={() => setHoveredAluno(athleteName)}
                      onMouseLeave={() => setHoveredAluno(null)}
                      sx={{
                        backgroundColor:
                          hoveredAluno === athleteName
                            ? 'rgba(255,255,255,0.08)'
                            : 'inherit',
                        transition: 'background-color 0.2s'
                      }}
                    >
                      {(mdUp
                        ? columns
                        : columns.filter(
                          (column) =>
                            column.field === 'athleteName' ||
                            column.field === 'routineName'
                        )).map((column) => {
                        if (column.field === 'athleteName') {
                          if (index === 0) {
                            return (
                              <TableCell
                                key={column.field}
                                align={column.align}
                                rowSpan={routines.length}
                                sx={{ color: '#fff', fontSize: 12, py: 1 }}
                              >
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Avatar imagePath={routine.imagePath} name={routine.athleteName} />
                                  <Typography fontSize={12} color="#fff">
                                    {routine.athleteName}
                                  </Typography>
                                </Box>
                              </TableCell>
                            )
                          } else {
                            return null
                          }
                        }

                        const value = routine[column.field]
                        return (
                          <TableCell
                            key={column.field}
                            align={column.align}
                            valign='middle'
                            sx={{ color: '#fff', fontSize: 12, width: column.width }}
                          >
                            {column.field === 'routineName' && (
                              <Tooltip title={routine.isOverdue ? 'Inativo' : 'Ativo'}>
                                <Circle style={{ fontSize: 14, color: routine.isOverdue ? 'gray' : 'green', paddingRight: 5, cursor: 'pointer' }}/>
                              </Tooltip>
                            )}
                            {column.field === 'engagementPercentage'
                              ? `${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`
                              : typeof value === 'number' && column.format
                                ? column.format(value)
                                : value}
                          </TableCell>
                        )
                      })}
                      {!mdUp
                        ? <TableCell>
                          <IconButton onClick={(event) => handleOpenPopover(routine, event)}>
                            <Visibility sx={{ color: '#fff' }} />
                          </IconButton>
                        </TableCell>
                        : ''
                      }
                    </TableRow>
                  )
                })
              })
              return rowsJSX
            })()}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[7, 25, 100]}
        component="div"
        count={filteredRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {anchorEl?.isConnected && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          PaperProps={{
            sx: {
              backgroundColor: '#333',
              color: '#fff',
              p: 2,
              borderRadius: 2,
              boxShadow: 4,
              minWidth: 220,
              fontSize: 13
            }
          }}
        >
          {selectedRoutine && (
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography><strong>Dt. Início:</strong> {selectedRoutine.startDate}%</Typography>
              <Typography><strong>% Engajamento: </strong>
                {selectedRoutine.engagementPercentage.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %
              </Typography>
              <Typography><strong>Quantidade de Treinos:</strong> {selectedRoutine.workoutQuantity}</Typography>
              <Typography><strong>Treinados:</strong> {selectedRoutine.doneWorkoutQuantity}</Typography>
              <Typography><strong>Não Treinados:</strong> {selectedRoutine.untrainedWorkoutQuantity}</Typography>
              <Typography><strong>Último Treino:</strong> {selectedRoutine.lastTrainedDate}</Typography>
              <Typography><strong>Dt. Vencimento:</strong> {selectedRoutine.endDate}</Typography>
            </Box>
          )}
        </Popover>
      )}
    </>
  )
}
